import React from 'react';
import { Checkbox } from './ui/checkbox';
import { 
  Mail, Cloud, Video, MessageSquare, FileText, 
  Upload, Calendar, Server, Code 
} from 'lucide-react';

// Custom provider icon component with humorous style
const ProviderIcon = ({ provider, color }) => {
  const providerData = {
    Google: { letter: 'G', emoji: '🔍' },
    Microsoft: { letter: 'M', emoji: '🪟' },
    Apple: { letter: 'A', emoji: '🍎' },
    Amazon: { letter: 'A', emoji: '📦' },
    Salesforce: { letter: 'S', emoji: '💬' },
    Zoom: { letter: 'Z', emoji: '🎥' },
    Dropbox: { letter: 'D', emoji: '📁' },
    WeTransfer: { letter: 'W', emoji: '🚀' },
    Autres: { letter: '?', emoji: '🤷' },
  };
  
  const data = providerData[provider] || { letter: '?', emoji: '' };
  
  return (
    <div 
      className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm relative"
      style={{ backgroundColor: `${color}15`, color }}
    >
      <span className="font-outfit">{data.letter}</span>
      <span className="absolute -top-1 -right-1 text-[10px]">{data.emoji}</span>
    </div>
  );
};

const iconMap = {
  mail: Mail,
  cloud: Cloud,
  video: Video,
  message: MessageSquare,
  'file-text': FileText,
  upload: Upload,
  calendar: Calendar,
  server: Server,
  code: Code,
};

const providerColorMap = {
  Google: '#4285F4',
  Microsoft: '#00A4EF',
  Apple: '#555555',
  Amazon: '#FF9900',
  Salesforce: '#4A154B',
  Zoom: '#2D8CFF',
  Dropbox: '#0061FF',
  WeTransfer: '#409FFF',
  Autres: '#64748B',
};

export const QuestionStep = ({ category, selectedServices, onServiceToggle }) => {
  const CategoryIcon = iconMap[category.icon] || Mail;

  return (
    <div className="animate-fade-in-up" data-testid={`question-step-${category.id}`}>
      {/* Category header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#0d2a47]/10 flex items-center justify-center">
          <CategoryIcon className="w-6 h-6 text-[#0d2a47]" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-slate-800 font-outfit">
            {category.label}
          </h2>
        </div>
      </div>

      {/* Question */}
      <p className="text-lg text-slate-600 mb-6">
        {category.question}
      </p>

      {/* Service options */}
      <div className="space-y-3">
        {category.services.map((service) => {
          const isSelected = selectedServices.includes(service.id);
          const providerColor = providerColorMap[service.provider] || '#64748B';

          return (
            <label
              key={service.id}
              className={`checkbox-card ${isSelected ? 'selected' : ''}`}
              data-testid={`service-option-${service.id}`}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onServiceToggle(service.id)}
                className="data-[state=checked]:bg-[#0d2a47] data-[state=checked]:border-[#0d2a47]"
                data-testid={`checkbox-${service.id}`}
              />
              
              <ProviderIcon 
                provider={service.provider}
                color={providerColor}
              />
              
              <div className="flex-1">
                <span className="font-medium text-slate-800">{service.name}</span>
              </div>
            </label>
          );
        })}

        {/* "None" option */}
        <label
          className={`checkbox-card ${selectedServices.includes(`none_${category.id}`) ? 'selected' : ''}`}
          data-testid={`service-option-none-${category.id}`}
        >
          <Checkbox
            checked={selectedServices.includes(`none_${category.id}`)}
            onCheckedChange={() => onServiceToggle(`none_${category.id}`)}
            className="data-[state=checked]:bg-[#006241] data-[state=checked]:border-[#006241]"
          />
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
            <span className="text-green-600 text-lg">✓</span>
          </div>
          <div className="flex-1">
            <span className="font-medium text-slate-800">
              Aucun service de ce type / Solution alternative
            </span>
          </div>
        </label>
      </div>
    </div>
  );
};

export default QuestionStep;
